//  Externals
import { BrowserAuthError, InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import Swal from 'sweetalert2';
//  Internals
import { loginRequest, protectedResources } from '../configs/msalAuthConfigs';

// Token cache to prevent multiple rapid acquisitions
interface TokenCache {
  token: string | null;
  expiresAt: number | null;
  pendingRequest: Promise<string> | null;
}

const tokenCache: TokenCache = {
  token: null,
  expiresAt: null,
  pendingRequest: null,
};

// Buffer time before token expiry to proactively refresh (5 minutes)
const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000;

/**
 * Thrown when the user's session can only be recovered by signing in again, so callers can tell
 * an expired session apart from a genuine failure.
 */
export class SessionExpiredError extends Error {
  public readonly reason: unknown;

  constructor(reason: unknown) {
    super('Your session has expired. Please sign in again.');
    this.name = 'SessionExpiredError';
    this.reason = reason;
  }
}

/*
 * Silent renewal runs in a hidden iframe against B2C. It fails for reasons signing in again will
 * fix: the B2C SSO session has lapsed, or the browser blocked the iframe (third-party cookie
 * restrictions in Safari and Firefox). Anything else is a real fault and should surface as-is.
 */
const INTERACTION_REQUIRED_CODES = ['monitor_window_timeout', 'block_iframe_reload', 'empty_window_error'];

const needsInteractiveSignIn = (error: unknown) => error instanceof InteractionRequiredAuthError || (error instanceof BrowserAuthError && INTERACTION_REQUIRED_CODES.includes(error.errorCode));

// Guard against several failing requests stacking up prompts on top of each other
let signInPromptOpen = false;

/**
 * Asks the user to sign in again, in a popup so whatever they have typed on the page survives.
 * Falls back to a full redirect if the popup is blocked, which costs them any unsaved work.
 */
const promptSignIn = async (pca: PublicClientApplication) => {
  if (signInPromptOpen) return;
  signInPromptOpen = true;

  try {
    const { isConfirmed } = await Swal.fire({
      icon: 'warning',
      title: 'Your session has expired',
      text: 'Sign in again to carry on. Anything you have already typed on this page will still be here.',
      showCancelButton: true,
      confirmButtonText: 'Sign in again',
      cancelButtonText: 'Not now',
      allowOutsideClick: false,
    });

    if (!isConfirmed) return;

    clearTokenCache();
    await pca.acquireTokenPopup({
      scopes: protectedResources.api.scopes,
      account: pca.getActiveAccount() || undefined,
    });

    Swal.fire({
      icon: 'success',
      position: 'center',
      showConfirmButton: false,
      text: 'You are signed back in. Please try that again.',
      timer: 2500,
    });
  } catch (error) {
    console.error('Interactive sign-in after session expiry failed, falling back to redirect', error);
    try {
      await pca.loginRedirect(loginRequest);
    } catch (redirectError) {
      console.error('Redirect to sign-in failed', redirectError);
    }
  } finally {
    signInPromptOpen = false;
  }
};

const getAccessToken = async (pca: PublicClientApplication) => {
  const now = Date.now();

  // Return cached token if still valid
  if (tokenCache.token && tokenCache.expiresAt && tokenCache.expiresAt > now) {
    return tokenCache.token;
  }

  // If a token request is already in progress, return that promise (deduplication)
  if (tokenCache.pendingRequest) {
    return tokenCache.pendingRequest;
  }

  // Create new token request
  const tokenPromise = (async () => {
    try {
      const activeAccount = pca.getActiveAccount();
      const accounts = pca.getAllAccounts();

      if (!activeAccount && accounts.length === 0) {
        /*
         * User is not signed in. Throw error or wait for user to login.
         * Do not attempt to log a user in outside of the context of MsalProvider
         */
        throw Error('No active account! Verify a user has been signed in and setActiveAccount has been called.');
      }

      const request = {
        scopes: protectedResources.api.scopes,
        account: activeAccount || accounts[0],
      };

      const authResult = await pca.acquireTokenSilent(request);

      // Cache the token with expiration
      const expiresOn = authResult.expiresOn ? authResult.expiresOn.getTime() : now + 3600 * 1000; // Default 1 hour
      tokenCache.token = authResult.accessToken;
      tokenCache.expiresAt = expiresOn - TOKEN_EXPIRY_BUFFER_MS; // Refresh 5 min before expiry

      return authResult.accessToken;
    } catch (error) {
      // Clear cache on error
      tokenCache.token = null;
      tokenCache.expiresAt = null;

      if (needsInteractiveSignIn(error)) {
        console.error('Silent token renewal failed, prompting the user to sign in again', error);
        // Deliberately not awaited so the in-flight request fails now and the spinner clears
        promptSignIn(pca);
        throw new SessionExpiredError(error);
      }

      throw error;
    } finally {
      // Clear pending request
      tokenCache.pendingRequest = null;
    }
  })();

  // Store the pending request to deduplicate simultaneous calls
  tokenCache.pendingRequest = tokenPromise;

  return tokenPromise;
};

// Export function to clear token cache (useful for logout or testing)
export const clearTokenCache = () => {
  tokenCache.token = null;
  tokenCache.expiresAt = null;
  tokenCache.pendingRequest = null;
};

export default getAccessToken;
